import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useMachine } from '@xstate/react';
import { useQuill } from 'react-quilljs';
import shortid from 'shortid';

import {
    PageContext,
    CommunityContext,
} from '../../../contexts/ContextCreator';
import postBoxMachine from './machine/postBoxMachine';
import ButtonPanel from '../ButtonPanel/ButtonPanel';
import PostBoxCTA from '../PostBoxCTA/PostBoxCTA';
import useOnClickOutside from '../../../utilities/hooks/useOnClickOutside';

// Quill imports
import unwrapMentionBlot from '../Modules/Mention/Mention';

import {
    containerVariants,
    ctaVariants,
    editorVariants,
    buttonPanelVariants,
    AnimationWrapper,
} from './animationVariants';

import { getProp, autoformatOpts } from './utils';

import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';

import './PostBoxContainer.scss';

const placeHolders = [
    'Share anything (even anonymously) ...',
    'Ask anything (even anonymously) ...',
    "What's on your mind today?",
];

const PostBoxContainer = ({ pageGroup = {} }) => {
    const { updateContentFromUPBResponse, defaultPostLocation } = useContext(
        CommunityContext
    );
    const { pageProps: { session: { id: userId = null } } = {} } = useContext(
        PageContext
    );

    const [state, send] = useMachine(postBoxMachine, {
        context: {
            communityTopicId: defaultPostLocation?.id ?? null,
            updateCommunityContext: updateContentFromUPBResponse,
        },
    });

    const [prompt, setPrompt] = useState(null);
    useEffect(() => {
        setPrompt(
            placeHolders[
                Math.floor(Math.random() * Math.floor(placeHolders.length))
            ]
        );
    }, []);

    const handleCtaClick = React.useCallback(() => {
        // dispatch open event
        send('OPEN');

        if (pageGroup.hash) {
            send('SELECT_POST_GROUP', { value: pageGroup.hash });
        }

        // emit metric
        const openMetric = {
            misc_event_type: 'postbox-cta-click',
            misc_event_count: 1,
            ...(userId ? { user_id: userId } : {}),
        };

        EmitMetric(openMetric);
    }, [userId, send, pageGroup.hash]);

    const handlePostClick = React.useCallback(() => {
        send('PUBLISH');
    }, [send]);

    const containerRef = useOnClickOutside(() => {
        // clear focus on editor to collapse keyboard for IOS devices
        const isFocused = document.activeElement.className.includes(
            'ql-editor'
        );
        if (isFocused) {
            document.activeElement.blur();
        }

        send('CLOSE');
    });

    const { quill, quillRef, Quill } = useQuill({
        modules: {
            toolbar: false,
            autoformat: {
                link: autoformatOpts.link,
                hashtag: autoformatOpts.hashtag,
            },
        },
        formats: ['bold', 'mention', 'hashtag', 'link'],
        placeholder: prompt,
        theme: 'snow',
    });

    if (Quill && !quill) {
        const Embed = Quill.import('blots/embed');
        const Mention = unwrapMentionBlot(Embed);

        const Autoformat = require('quill-autoformat').default;

        const quillModules = {
            'modules/autoformat': Autoformat,
            'formats/mention': Mention,
        };

        Quill.register(quillModules, true);
    }

    // event handlers
    const handleBlur = React.useCallback(() => {}, []);

    const handleChange = React.useCallback(
        ({ currentContents, html, postText }) => {
            const links = currentContents.ops
                .map((op) => getProp(op, 'attributes.link'))
                .filter(Boolean);

            const update = {
                postText,
                html,
                links,
            };

            send('EDITOR_UPDATE', update);
            // @TODO dispatch an event that clears the hash.

            // TODO: fix dirty/clean logic
            // this only fires with an empty editor
            // so its not truly 'dirty' i.e. user has made changes
            if (postText.length === 0) {
                send('EDITOR_CLEAN');
            }
        },

        [send]
    );

    const handleFocus = React.useCallback(() => send('ACTIVE'), [send]);

    React.useEffect(() => {
        if (!quill) {
            return;
        }

        if (state.context.postSuccess) {
            quill.root.innerHTML = '';
        }
    }, [quill, state.context.postSuccess]);

    React.useEffect(() => {
        if (!quill) {
            return;
        }

        const onEditorChange = (event, _delta, _oldDelta) => {
            const currentContents = quill.getContents();
            const postText = quill.getText(0).trim();
            const postTextLength = postText.length;

            const isBlurEvent = event === 'selection-change' && _delta === null;
            const isFocusEvent =
                event === 'selection-change' &&
                _delta !== null &&
                _oldDelta === null;
            const isChangeEvent = event === 'text-change';

            const blurOrFocusPayload = {
                currentContents,
                textLength: postTextLength,
            };

            if (isBlurEvent && quill) {
                handleBlur(blurOrFocusPayload);
            }

            if (isFocusEvent && quill) {
                handleFocus(blurOrFocusPayload);
            }

            if (isChangeEvent && quill) {
                if (state.context.error.length > 0) {
                    send('CLEAR_ERROR');
                }

                handleChange({
                    currentContents,
                    postText,
                    html: quill.root.innerHTML,
                    textLength: postTextLength,
                });
            }
        };

        quill.on('editor-change', onEditorChange);

        return () => {
            if (quill) {
                quill.off('editor-change', onEditorChange);
            }
        };
    }, [
        quill,
        send,
        handleBlur,
        handleChange,
        handleFocus,
        state.context.error,
    ]);

    const insertHashtag = React.useCallback(() => {
        if (quill) {
            if (!quill.hasFocus()) quill.focus();

            const range = quill.getSelection();

            const isCollapsed = range?.length === 0;
            if (isCollapsed) {
                quill.insertText(range.index, '#', 'silent');
                quill.setSelection(range.index + 1, 0, 'silent');
            }
        }
        const hashtagMetric = {
            misc_event_type: 'editor-hashtag-click',
            misc_event_count: 1,
            ...(userId ? { user_id: userId } : {}),
        };

        EmitMetric(hashtagMetric);
    }, [quill, userId]);

    const autoFocusEditor = React.useCallback(() => {
        if (!quill || quill.hasFocus()) return;

        quill.focus();
    }, [quill]);

    const postButtonDisabled =
        state.value === 'closed' ||
        !state.context.isDirty ||
        state.context.isInactive ||
        !userId; // disable posting for unauthed users

    const disablePhoto =
        state.context.link?.url?.length > 0 || state.context.image?.length > 0;

    return (
        <motion.div
            className="component-UnifiedPostBox-PostBoxContainer"
            id="unified-post-box"
            ref={containerRef}
            initial={state.value}
            variants={containerVariants}
            animate={state.value}
            layout
        >
            {/**
             * The keyboard would not be triggered on IOS devices when auto-focus with our text-editor
             * On IOS devices, html element has to satisfy the following conditions for keyboard to be triggered when focus
             *     1. editable html element, i.e: input, textarea
             *     2. present on screen, i.e can't have "display: none" property
             * Work Around:
             * Placing an invisible input element as placeholder for focusing and triggering keyboard
             * Focus will be first drawn to this mock input, then re-focus to our text-editor
             */}
            <input type="text" id="mock-input-for-ios-focus" />
            <div className="post-box-top-accent" />
            <div className="post-box-main-content">
                <AnimationWrapper
                    component={PostBoxCTA}
                    variants={ctaVariants}
                    initial={state.value}
                    animate={state.value}
                    onClick={handleCtaClick}
                    placeholderText={prompt}
                />
                <motion.div
                    className="component-UnifiedPostBox-PostBoxEditor"
                    variants={editorVariants}
                    initial={state.value}
                    animate={state.value}
                    onAnimationComplete={autoFocusEditor}
                    layout
                >
                    <div ref={quillRef} />
                </motion.div>
                <AnimationWrapper
                    component={ButtonPanel}
                    className="button-panel-wrapper"
                    variants={buttonPanelVariants}
                    initial={state.value}
                    animate={state.value}
                    dispatch={send}
                    insertHashtag={insertHashtag}
                    isAnonymousPost={state.context.isAnonymousPost}
                    communityGroupHash={state.context.communityGroupHash}
                    disablePhoto={disablePhoto}
                    pageGroup={pageGroup}
                    embedlyUrl={state.context.link.url}
                    previewDisabled={state.context.isPreviewDisabled}
                    image={state.context.image}
                />
            </div>
            {state.context.uploadError?.length > 0 && (
                <div className="post-error-message">
                    {state.context.uploadError.map((err) => (
                        <span key={shortid()}>{err}</span>
                    ))}
                </div>
            )}
            {state.matches('error') && state.context.error.length > 0 && (
                <div className="post-error-message">
                    {state.context.error.map((err) => (
                        <span key={shortid()}>{err}</span>
                    ))}
                </div>
            )}
            <button
                type="button"
                disabled={postButtonDisabled}
                onClick={handlePostClick}
                className="post-button"
            >
                Post
            </button>
        </motion.div>
    );
};

PostBoxContainer.propTypes = {
    pageGroup: PropTypes.shape({
        hash: PropTypes.string,
        name: PropTypes.string,
    }),
};

PostBoxContainer.defaultProps = {
    pageGroup: {},
};

PostBoxContainer.displayName = 'PostBoxContainer';

export default PostBoxContainer;
